import React from 'react';
import { Card, CardContent } from 'src/components/ui/card';
import { RequestStatusCell } from 'src/features/requests/request-table/request-status-cell';
import { RequestRetentionTestDate } from 'src/features/requests/request-retention-test-date';
import { cn } from 'src/lib/utils';
import { useTranslation } from 'react-i18next';
import dompurify from 'dompurify';
import { Str } from 'src/helpers';
import { totalLengthToString } from 'src/features/requests/request-fields/request-field.helper';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useEditorReadOnlyAccess } from 'src/features/requests/hooks/use-editor-read-only-access';
import { OrderNotes } from 'src/pages/RevisionsPage/UpdatedDesign/RevisionsContentBottom/OrderNotes';
import RequestActivityList from 'src/features/requests/request-activity/request-activity-list';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { formatDueDate } from 'src/features/requests/request.helper';
import { RenderForAdmin } from 'src/components/Middleware';
import { roles } from 'src/api/services/RoleClient';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Button } from 'src/components/ui/button';
import { Eye, Pencil } from 'lucide-react';
import { useUpdateRequestDueDate } from 'src/features/requests/update-request-due-date-dialog/use-update-request-due-date';
import { ComposedMediaPreviewDialog } from 'src/features/media/composed-media-preview-dialog';
import { decorateMedia } from 'src/features/media';
import { DialogTrigger } from 'src/components/ui/dialog';
import { UpdateRequestDueDateDialog } from 'src/features/requests/update-request-due-date-dialog/update-request-due-date-dialog';
import { useProjectFilesUrlModal } from 'src/features/requests/project-files-url-modal';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectTitleCard } from 'src/features/requests/project-title-card';
import { useRequestQuery } from 'src/api/queries';

const RevisionOutcomeContentBottom: React.FC = () => {
  const { t } = useTranslation();
  const outcome = useDecoratedRequestContext();
  // Outcomes are leaves, so this is the more common path for the covering editor
  // #2653 describes — RevisionsContentTop is shared and already shows the badge.
  const isReadOnly = useEditorReadOnlyAccess();
  const {
    id,
    type,
    description,
    editor_note,
    due_date,
    total_length,
    title,
    video_template,
    resolutions,
    isCurrentlyEditing,
    project_files_url,
    parent_id,
  } = outcome;

  // Parent project fetch — match request-outcome-page so both the outcome
  // detail view and this Revisions view share the same query cache key.
  const parentQuery = useRequestQuery(parent_id ?? '', {
    enabled: !!parent_id,
  });
  const { setRequestDueDateDialogOpen } = useUpdateRequestDueDate();
  const { openModal } = useProjectFilesUrlModal();
  const queryClient = useQueryClient();

  // Handler for updating the project files URL
  const handleEditProjectFilesUrl = () => {
    openModal({
      requestId: id,
      initialValue: project_files_url || '',
      title: t('Edit Suite Request Folder Location')!,
      description: t('Update the URL to the project files')!,
      onSubmit: () => {
        // Refresh the request data after update
        queryClient.invalidateQueries(['request', id]);
      },
    });
  };

  return (
    <div className={'tw-flex tw-flex-col tw-gap-6'}>
      {parentQuery.data && parent_id && (
        <ProjectTitleCard title={parentQuery.data.title} parentId={parent_id} />
      )}

      <div className={'tw-flex tw-flex-col tw-gap-4'}>
        <h3 className="tw-text-2xl tw-font-semibold">{t('Outcome info')}</h3>
        <Card>
          <CardContent className={'tw-px-6 tw-py-0 md:tw-py-6'}>
            {video_template?.title && (
              <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
                <div className={'tw-col-span-1 tw-mb-0 tw-text-lg tw-font-bold tw-text-text'}>
                  {t('Style name')}
                </div>
                <div className={'tw-col-span-2 tw-flex tw-items-center tw-gap-2'}>
                  <span className={'tw-break-words'}>{video_template?.title}</span>
                  {video_template?.template && (
                    <ComposedMediaPreviewDialog media={decorateMedia(video_template.template)}>
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <DialogTrigger asChild>
                              <Button variant={'ghost'} size={'sm'}>
                                <Eye size={16} className={'tw-mr-1'} />
                                {t('Preview')}
                              </Button>
                            </DialogTrigger>
                          </TooltipTrigger>
                          <TooltipContent>{t('Preview video style')}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </ComposedMediaPreviewDialog>
                  )}
                </div>
              </CardContentRow>
            )}
            <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
              <div className={'tw-col-span-1 tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('Request name')}
              </div>
              <div className={'tw-col-span-2 tw-break-words'}>
                {title ?? t('common:unset', { defaultValue: '-' })}
              </div>
            </CardContentRow>
            {resolutions?.main_size && (
              <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
                <div className={'tw-col-span-1 tw-mb-0 tw-text-lg tw-font-bold tw-text-text'}>
                  {t('Size')}
                </div>
                <div className={'tw-col-span-2 tw-break-words'}>{resolutions.main_size}</div>
              </CardContentRow>
            )}
            {resolutions?.resize && resolutions.resize.length > 0 && (
              <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
                <div className={'tw-col-span-1 tw-mb-0 tw-text-lg tw-font-bold tw-text-text'}>
                  {t('Resizes')}
                </div>
                <div className={'tw-col-span-2 tw-break-words'}>
                  {resolutions.resize.join(', ')}
                </div>
              </CardContentRow>
            )}
            <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
              <div className={'tw-col-span-1 tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('Status')}
              </div>
              <div className={'tw-col-span-2'}>
                {/* Shares the resolution and the re-edit `Repeat` trail with
                    the list, kanban and revisions-header pills, so this row
                    keeps flagging a re-edit round once its pill turns yellow
                    "Editing". */}
                <RequestStatusCell request={outcome} size={'default'} />
              </div>
            </CardContentRow>
            <div className={'tw-border-b tw-border-neutral-100 tw-py-2 empty:tw-hidden'}>
              <RequestRetentionTestDate request={outcome} />
            </div>
            <CardContentRow
              className={'tw-min-w-0 tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}
            >
              <div className={'tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('Video type')}
              </div>
              <div className={'tw-col-span-2 tw-break-words'}>
                {t(`models/request:type.${type}`, {
                  defaultValue: t('common:unknown', {
                    defaultValue: 'Unknown',
                  }),
                })}
              </div>
            </CardContentRow>
            <CardContentRow
              className={'tw-min-w-0 tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}
            >
              <div className={'tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('Total length')}
              </div>
              <div className={'tw-col-span-2 tw-break-words'}>
                {totalLengthToString(total_length) || t('common:unset', { defaultValue: '-' })}
              </div>
            </CardContentRow>
            <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
              <div
                className={'tw-mb-0 tw-align-middle tw-text-lg tw-font-bold tw-text-text-strong'}
              >
                {t('Description')}
              </div>
              <div
                className={'tw-col-span-2 tw-whitespace-pre-line tw-break-words'}
                dangerouslySetInnerHTML={{
                  __html: Str.wrapHrefs(
                    dompurify.sanitize(
                      description?.video_request_description ??
                        t('common:unset', {
                          defaultValue: '-',
                        })!,
                    ),
                  ),
                }}
              />
            </CardContentRow>
            <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
              <div className={'tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('First message')}
              </div>
              <div className={'tw-col-span-2 tw-break-words'}>
                {editor_note ?? t('common:unset', { defaultValue: '-' })}
              </div>
            </CardContentRow>
            <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
              <div className={'tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('Suite Request Folder Location')}
              </div>
              <div className={'tw-col-span-2 tw-flex tw-items-start tw-break-words'}>
                {project_files_url ? (
                  <a
                    href={project_files_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-hover:text-blue-800 tw-break-all tw-text-blue-600 tw-underline"
                  >
                    {project_files_url}
                  </a>
                ) : (
                  <span>{t('common:unset', { defaultValue: '-' })}</span>
                )}

                {/* updateProjectFilesUrl is assignment-scoped server-side, so a
                    covering editor gets the link to read and nothing else. */}
                {!isReadOnly && (
                  <RenderForAdmin and={[roles.teamLead, roles.editor]}>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type={'button'}
                            variant={'ghost'}
                            size={'iconSm'}
                            className={'tw-ms-2 tw-text-neutral-600 tw-transition-colors'}
                            onClick={() => handleEditProjectFilesUrl()}
                          >
                            <Pencil className={'tw-h-3.5 tw-w-3.5'} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('Edit Suite Request Folder Location')}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </RenderForAdmin>
                )}
              </div>
            </CardContentRow>
            <CardContentRow className={'tw-gap-6 tw-border-b tw-border-neutral-100 tw-py-4'}>
              <div className={'tw-mb-0 tw-text-lg tw-font-bold tw-text-text-strong'}>
                {t('Estimated due date')}
              </div>
              <div className={'tw-col-span-2 tw-break-words'}>
                {due_date ? formatDueDate(t, due_date) : t('-')}

                {isCurrentlyEditing && (
                  <RenderForAdmin and={[roles.teamLead]}>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type={'button'}
                            variant={'ghost'}
                            size={'iconSm'}
                            className={'tw-ms-2 tw-text-neutral-600 tw-transition-colors'}
                            onClick={() => {
                              setRequestDueDateDialogOpen(id);
                            }}
                          >
                            <Pencil className={'tw-size-4'} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('Edit field')}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </RenderForAdmin>
                )}
              </div>
            </CardContentRow>
          </CardContent>
        </Card>
      </div>

      <div className={'tw-flex tw-flex-col tw-gap-4'}>
        <h3 className="tw-text-2xl tw-font-semibold">
          {t('revisions.order.notes.section_title', { ns: 'pages/shared' })}
        </h3>

        <Card>
          <OrderNotes />
        </Card>
      </div>

      <div className={'tw-flex tw-flex-col tw-gap-4'}>
        <h3 className="tw-text-2xl tw-font-semibold">
          {t('pages/shared:revisions.activity', 'Activity')}
        </h3>

        <Card>
          <CardContent className={'tw-p-4 md:tw-p-6'}>
            <ScrollArea>
              <div className="tw-max-h-96">
                <RequestActivityList requestId={id} />
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <UpdateRequestDueDateDialog />
    </div>
  );
};

const CardContentRow = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        {...props}
        ref={ref}
        className={cn(
          'tw-flex tw-h-auto tw-flex-col tw-font-medium md:tw-grid md:tw-grid-cols-3 md:tw-items-center',
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

export { RevisionOutcomeContentBottom };
